import React, { useEffect, useMemo, useRef, useState } from "react";
import "./dateRangePicker.css";

const formatDisplay = (value) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("es-CL", { day: "2-digit", month: "short", year: "numeric" })
    .format(new Date(`${value}T12:00:00`));
};

const localDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseLocalDate = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildCalendarDays = (viewDate) => {
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const first = new Date(year, month, 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - mondayOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return {
      iso: localDate(date),
      day: date.getDate(),
      currentMonth: date.getMonth() === month,
    };
  });
};

function DateRangePicker({
  value,
  onChange,
  iconOnly = false,
  firstAvailableDate = "2000-01-01",
}) {
  const normalizedFirstAvailableDate = /^\d{4}-\d{2}-\d{2}$/.test(String(firstAvailableDate || ""))
    ? firstAvailableDate
    : localDate(new Date());
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [customOpen, setCustomOpen] = useState(false);
  const [selectingEnd, setSelectingEnd] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const seed = parseLocalDate(value?.start) || new Date();
    return new Date(seed.getFullYear(), seed.getMonth(), 1);
  });
  const containerRef = useRef(null);
  const calendarDays = useMemo(() => buildCalendarDays(viewDate), [viewDate]);

  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.key === "Escape") setOpen(false);
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("keydown", close);
    document.addEventListener("mousedown", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.removeEventListener("mousedown", close);
    };
  }, [open]);

  const choosePreset = (preset) => {
    const todayDate = new Date();
    let start = new Date(todayDate);
    let end = new Date(todayDate);
    if (preset === "yesterday") {
      start.setDate(start.getDate() - 1);
      end = new Date(start);
    } else if (preset === "last7") {
      start.setDate(start.getDate() - 6);
    } else if (preset === "last15") {
      start.setDate(start.getDate() - 14);
    } else if (preset === "month") {
      start = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
    }
    const nextRange = {
      start: preset === "all" ? normalizedFirstAvailableDate : localDate(start),
      end: localDate(end),
    };
    setDraft(nextRange);
    setCustomOpen(false);
    setSelectingEnd(false);
    onChange(nextRange);
    setOpen(false);
  };
  const openCustomRange = () => {
    const seed = parseLocalDate(value?.start) || new Date();
    setDraft(value);
    setViewDate(new Date(seed.getFullYear(), seed.getMonth(), 1));
    setSelectingEnd(false);
    setCustomOpen(true);
  };
  const selectCalendarDay = (iso) => {
    if (!selectingEnd || !draft.start) {
      setDraft({ start: iso, end: iso });
      setSelectingEnd(true);
      return;
    }
    setDraft(iso < draft.start
      ? { start: iso, end: draft.start }
      : { start: draft.start, end: iso });
    setSelectingEnd(false);
  };

  const valid = draft.start && draft.end && draft.start <= draft.end;
  const label = value.start === value.end
    ? formatDisplay(value.start)
    : `${formatDisplay(value.start)} – ${formatDisplay(value.end)}`;
  const inclusiveDays = Math.round(
    (new Date(`${value.end}T12:00:00`) - new Date(`${value.start}T12:00:00`)) / 86400000
  ) + 1;
  const today = localDate(new Date());
  const yesterdayDate = new Date();
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterday = localDate(yesterdayDate);
  const monthStart = localDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  let selectedPreset = null;
  if (value.start === today && value.end === today) selectedPreset = "today";
  else if (value.start === yesterday && value.end === yesterday) selectedPreset = "yesterday";
  else if (value.end === today && inclusiveDays === 7) selectedPreset = "last7";
  else if (value.end === today && inclusiveDays === 15) selectedPreset = "last15";
  else if (value.start === monthStart && value.end === today) selectedPreset = "month";
  else if (value.start === normalizedFirstAvailableDate && value.end === today) selectedPreset = "all";
  const customSelected = customOpen || selectedPreset === null;

  return (
    <div className="date-range-picker" ref={containerRef}>
      <button
        type="button"
        className={`date-range-picker__trigger${iconOnly ? " date-range-picker__trigger--icon" : ""}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Seleccionar periodo: ${label}`}
        title={label}
      >
        <span className="material-symbols-outlined" aria-hidden="true">date_range</span>
        {!iconOnly && <span>{label}</span>}
        {!iconOnly && (
          <span className="material-symbols-outlined date-range-picker__chevron" aria-hidden="true">
            {open ? "expand_less" : "expand_more"}
          </span>
        )}
      </button>

      {open && (
        <div className="date-range-picker__popover" role="dialog" aria-label="Seleccionar rango de fechas">
          <div className="date-range-picker__title">
            <div><span>Periodo</span><strong>Seleccionar rango</strong></div>
            <span className="material-symbols-outlined" aria-hidden="true">calendar_month</span>
          </div>
          <div className="date-range-picker__preset-list" role="listbox" aria-label="Periodos rápidos">
            {[
              { id: "today", label: "Hoy", detail: "Solo el día actual" },
              { id: "yesterday", label: "Ayer", detail: "Solo el día anterior" },
              { id: "last7", label: "Últimos 7 días", detail: "Incluye hoy" },
              { id: "last15", label: "Últimos 15 días", detail: "Incluye hoy" },
              { id: "month", label: "Este mes", detail: "Desde el primer día del mes" },
              { id: "all", label: "Todo el historial", detail: "Todos los registros disponibles" },
            ].map((preset) => (
              <button
                type="button"
                role="option"
                aria-selected={selectedPreset === preset.id}
                key={preset.id}
                onClick={() => choosePreset(preset.id)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  {selectedPreset === preset.id ? "radio_button_checked" : "radio_button_unchecked"}
                </span>
                <span><strong>{preset.label}</strong><small>{preset.detail}</small></span>
              </button>
            ))}
            <button
              type="button"
              role="option"
              aria-selected={customSelected}
              onClick={() => {
                if (customOpen) setCustomOpen(false);
                else openCustomRange();
              }}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                {customSelected ? "radio_button_checked" : "radio_button_unchecked"}
              </span>
              <span><strong>Rango personalizado</strong><small>{label}</small></span>
              <span className="material-symbols-outlined" aria-hidden="true">
                {customOpen ? "expand_less" : "expand_more"}
              </span>
            </button>
          </div>

          {customOpen && (
            <>
              <div className="date-range-picker__calendar">
                <div className="date-range-picker__calendar-head">
                  <button
                    type="button"
                    onClick={() => setViewDate((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
                    aria-label="Mes anterior"
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">chevron_left</span>
                  </button>
                  <strong>{viewDate.toLocaleDateString("es-CL", { month: "long", year: "numeric" })}</strong>
                  <button
                    type="button"
                    onClick={() => setViewDate((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
                    aria-label="Mes siguiente"
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">chevron_right</span>
                  </button>
                </div>
                <div className="date-range-picker__weekdays" aria-hidden="true">
                  {["L", "M", "M", "J", "V", "S", "D"].map((day, index) => (
                    <span key={`${day}-${index}`}>{day}</span>
                  ))}
                </div>
                <div className="date-range-picker__days">
                  {calendarDays.map((day) => {
                    const isStart = day.iso === draft.start;
                    const isEnd = day.iso === draft.end;
                    const inRange = draft.start && draft.end
                      && day.iso >= draft.start && day.iso <= draft.end;
                    return (
                      <button
                        type="button"
                        key={day.iso}
                        className={[
                          !day.currentMonth ? "is-muted" : "",
                          inRange ? "is-in-range" : "",
                          isStart ? "is-start" : "",
                          isEnd ? "is-end" : "",
                          day.iso === today ? "is-today" : "",
                        ].filter(Boolean).join(" ")}
                        onClick={() => selectCalendarDay(day.iso)}
                        aria-pressed={Boolean(isStart || isEnd)}
                        aria-label={formatDisplay(day.iso)}
                      >
                        <span>{day.day}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="date-range-picker__selection-hint">
                  {selectingEnd
                    ? "Ahora selecciona la fecha final"
                    : `${formatDisplay(draft.start)} – ${formatDisplay(draft.end)}`}
                </p>
              </div>
              {!valid && <p className="date-range-picker__error">La fecha final debe ser posterior a la inicial.</p>}
              <div className="date-range-picker__actions">
                <button type="button" onClick={() => { setDraft(value); setCustomOpen(false); setSelectingEnd(false); }}>Cancelar</button>
                <button type="button" disabled={!valid || selectingEnd} onClick={() => { onChange(draft); setCustomOpen(false); setOpen(false); }}>Aplicar</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
